import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import { getOrphanTestCycleInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';

const CHART_HEIGHT = 260;

const RANGE_COLOR_KEY = {
  '15-30 days': 'warning',
  '>30 days': 'error',
};

const TestCycleInactiveCard = ({ sx = {} }) => {
  const { projectId } = useParams();
  const theme = useTheme();

  const {
    data,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.project.orphanTestCycleInsights(projectId),
    queryFn: () => getOrphanTestCycleInsights(projectId),
    enabled: !!projectId,
  });

  const distribution = data?.distribution || [];
  const insightMessage = data?.insight;

  const chartData = useMemo(
    () =>
      distribution.map((entry) => ({
        range: entry.range,
        count: entry.count,
        colorKey: RANGE_COLOR_KEY[entry.range] || 'primary',
      })),
    [distribution]
  );

  const hasData = chartData.some((entry) => entry.count > 0);

  if (loading) {
    return (
      <DetailsCard title="Test Cycle Inactivity" sx={sx}>
        <Loader minHeight={CHART_HEIGHT} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="Test Cycle Inactivity" sx={sx}>
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to load test cycle inactivity insights.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard title="Test Cycle Inactivity" contentSx={{ p: 0 }} sx={sx}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                height: CHART_HEIGHT,
                display: 'flex',
                alignItems: hasData ? 'stretch' : 'center',
                justifyContent: hasData ? 'stretch' : 'center',
              }}
            >
              {hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                    barCategoryGap="35%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                    <XAxis dataKey="range" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                    <Tooltip
                      cursor={{ fill: alpha(theme.palette.primary.main, 0.06) }}
                      contentStyle={{
                        borderRadius: theme.shape.borderRadius,
                        borderColor: theme.palette.divider,
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    />
                    <Bar dataKey="count" name="Inactive Test Cycles" radius={[4, 4, 0, 0]} maxBarSize={64}>
                      {chartData.map((entry) => (
                        <Cell key={entry.range} fill={theme.palette[entry.colorKey].main} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No inactive test cycles found for the selected period.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: `${theme.shape.borderRadius}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Typography variant="caption" fontWeight={700} color="info.dark" display="block">
                AI Insight
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {insightMessage || 'Not enough data yet to generate an insight for this period.'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </DetailsCard>
  );
};

export default TestCycleInactiveCard;